import React, { useState, useEffect, useRef } from 'react';
import { User, CheckCircle, Upload, Phone, MapPin, Crown, Sparkles, ArrowLeft, Lock } from 'lucide-react';
import API from '../services/API';
import DateSelect from './DateSelect';

const CUR_YEAR = new Date().getFullYear();

/**
 * Form Đăng ký đại lý mới + Cập nhật thông tin (cùng 1 form — xem docs/logic-dang-ky-cap-nhat-dai-ly.md).
 * mode='register': người mới điền hồ sơ → màn Chúc mừng (chờ admin duyệt).
 * mode='update'  : đại lý đã đăng nhập sửa thông tin (đổ sẵn dữ liệu; đổi cấp trên → gửi yêu cầu chờ duyệt).
 *
 * Cấp trên (5) + Thủ lĩnh (6) KHÔNG nhập tay — tự hiện khi gõ đúng SĐT cấp trên (4).
 * F mấy / Số F1 / Thủ lĩnh do hệ thống tự tính → KHÔNG có ô nhập.
 */
const NumberBadge = ({ n, color }) => (
  <span className="inline-flex items-center justify-center w-6 h-6 rounded-md text-white text-xs font-bold mr-2 shrink-0" style={{ background: color }}>{n}</span>
);

const onlyName = (codeFb) => (codeFb && codeFb.includes(' - ') ? codeFb.split(' - ').slice(1).join(' - ') : codeFb || '');
const maskPhone = (p) => (p && p.length >= 6 ? `${p.slice(0, 2)}****${p.slice(-4)}` : p || '');

const RegisterPage = ({
  mode = 'register',
  phoneNumber,
  accessToken,
  onBackToLogin,
  onDone,
  setError,
  error,
  loading,
  setLoading,
}) => {
  const [form, setForm] = useState({
    phone: phoneNumber || '',
    avatar: '',
    name: '',
    birthday: '',
    facebook: '',
    parentPhone: '',
    joinDate: '',
    city: '',
  });
  const [originalParentPhone, setOriginalParentPhone] = useState('');
  const [joinDateLocked, setJoinDateLocked] = useState(false); // BE: approved + đã có join_date
  const [parentDisplay, setParentDisplay] = useState('');   // "1194 - Trần Thế Ngân"
  const [leaderDisplay, setLeaderDisplay] = useState('');   // "Võ Thị Bích Phương"
  const [parentLookupState, setParentLookupState] = useState('idle'); // idle|loading|found|notfound
  const [avatarPreview, setAvatarPreview] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [cities, setCities] = useState([]);
  const [errors, setErrors] = useState({});
  const [congrats, setCongrats] = useState(null); // dữ liệu màn Chúc mừng (register)
  const [doneMsg, setDoneMsg] = useState('');      // thông báo sau khi cập nhật (update)
  const avatarRef = useRef(null);

  const isUpdate = mode === 'update';

  // Tải danh sách tỉnh thành (bảng city)
  useEffect(() => {
    API.getCities().then((list) => setCities(Array.isArray(list) ? list : [])).catch(() => setCities([]));
  }, []);

  // Update mode: đổ sẵn hồ sơ hiện tại
  useEffect(() => {
    if (!isUpdate || !accessToken) return;
    (async () => {
      try {
        const p = await API.getMyAgentProfile(accessToken);
        setForm((prev) => ({
          ...prev,
          phone: p.sdt || '',
          avatar: p.avatar || '',
          name: p.name || '',
          birthday: p.birthday ? String(p.birthday).slice(0, 10) : '',
          facebook: p.facebook || '',
          parentPhone: p.phoneParent || '',
          joinDate: p.joinDate ? String(p.joinDate).slice(0, 10) : '',
          city: p.city || '',
        }));
        if (p.avatar) setAvatarPreview(p.avatar);
        setOriginalParentPhone(p.phoneParent || '');
        setJoinDateLocked(!!p.joinDateLocked);
        if (p.parentCodeFacebook) { setParentDisplay(p.parentCodeFacebook); setParentLookupState('found'); }
        if (p.leaderCodeFacebook) setLeaderDisplay(onlyName(p.leaderCodeFacebook));
      } catch (e) {
        setError && setError(e.message);
      }
    })();
  }, [isUpdate, accessToken, setError]);

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: null }));
  };

  // Tra cấp trên khi SĐT cấp trên đủ 10 số (debounce)
  useEffect(() => {
    const phone = (form.parentPhone || '').replace(/\s/g, '');
    if (phone.length < 10) { setParentDisplay(''); setLeaderDisplay(''); setParentLookupState('idle'); return; }
    let cancelled = false;
    setParentLookupState('loading');
    const t = setTimeout(async () => {
      try {
        const info = await API.getUplineInfo(phone);
        if (cancelled) return;
        if (info && info.found) {
          setParentDisplay(info.parentCodeFacebook || '');
          setLeaderDisplay(onlyName(info.leaderCodeFacebook || ''));
          setParentLookupState('found');
          setErrors((prev) => ({ ...prev, parentPhone: null }));
        } else {
          setParentDisplay(''); setLeaderDisplay(''); setParentLookupState('notfound');
        }
      } catch {
        if (!cancelled) { setParentDisplay(''); setLeaderDisplay(''); setParentLookupState('notfound'); }
      }
    }, 450);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.parentPhone]);

  const handleAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const v = API.validateImageFile(file);
    if (!v.isValid) { setError && setError(v.error); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
    try {
      setIsUploading(true);
      setError && setError('');
      const up = await API.uploadImage(file);
      if (up.success) { setField('avatar', up.fileUrl); setAvatarPreview(up.fileUrl); }
      else setError && setError('Không thể tải ảnh. Vui lòng thử lại.');
    } catch (err) {
      setError && setError(err.message || 'Lỗi khi tải ảnh');
    } finally {
      setIsUploading(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Vui lòng nhập họ và tên';
    if (!form.birthday) e.birthday = 'Vui lòng chọn ngày sinh';
    if (!form.facebook.trim()) e.facebook = 'Vui lòng nhập tên hay dùng';
    if (!form.parentPhone.trim()) e.parentPhone = 'Vui lòng nhập SĐT cấp trên';
    else if (parentLookupState === 'notfound') e.parentPhone = 'Không tìm thấy cấp trên trong hệ thống đại lý';
    else if (parentLookupState !== 'found') e.parentPhone = 'Đang kiểm tra cấp trên, vui lòng đợi';
    if (!form.joinDate) e.joinDate = 'Vui lòng chọn ngày tham gia';
    if (!form.city) e.city = 'Vui lòng chọn tỉnh thành';
    if (!form.avatar && !avatarPreview) e.avatar = 'Vui lòng chọn ảnh đại diện';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmitRegister = async () => {
    if (loading) return;
    setError && setError('');
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await API.registerAgent({
        phone: form.phone,
        name: form.name.trim(),
        facebook: form.facebook.trim(),
        birthday: form.birthday,
        avatar: form.avatar,
        parentPhone: form.parentPhone.replace(/\s/g, ''),
        joinDate: form.joinDate,
        city: form.city,
      });
      setCongrats(res);
    } catch (err) {
      setError && setError(err.message || 'Đăng ký thất bại, vui lòng thử lại');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitUpdate = async () => {
    if (loading) return;
    setError && setError('');
    if (!validate()) return;
    setLoading(true);
    try {
      await API.updateProfileBasic({
        name: form.name.trim(),
        facebook: form.facebook.trim(),
        birthday: form.birthday,
        avatar: form.avatar,
        city: form.city,
        joinDate: form.joinDate,
      }, accessToken);

      const newParent = form.parentPhone.replace(/\s/g, '');
      if (newParent && newParent !== originalParentPhone) {
        await API.requestChangeParent(newParent, accessToken);
        setDoneMsg('Đã lưu thông tin. Yêu cầu đổi cấp trên đã gửi — chờ công ty duyệt.');
      } else {
        setDoneMsg('Đã lưu thông tin thành công.');
      }
    } catch (err) {
      setError && setError(err.message || 'Cập nhật thất bại, vui lòng thử lại');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Màn CHÚC MỪNG (register) ----------
  if (congrats) {
    const lvl = Number(congrats.level);
    const fLabel = lvl > 0 && lvl < 50 ? `F${lvl}` : 'Đại lý';
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
          <div className="bg-gradient-to-r from-brand-600 to-emerald-500 text-white text-center px-6 py-7 relative">
            <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-90" />
            <h1 className="text-xl font-bold">Chúc mừng {congrats.facebook || congrats.name}!</h1>
            <p className="text-brand-50 mt-1 text-sm">Bạn đã đăng ký tham gia Yody Phương Anh</p>
            <div className="mt-3 inline-block bg-white/20 rounded-xl px-5 py-2">
              <div className="text-xs text-brand-50">Mã số của bạn</div>
              <div className="text-3xl font-extrabold tracking-wide">{congrats.code || '—'}</div>
            </div>
          </div>

          <div className="p-6 space-y-3">
            <div className="flex items-center gap-3">
              <img src={avatarPreview || congrats.avatar} alt="avatar" className="w-14 h-14 rounded-full object-cover border-2 border-brand-200" />
              <div>
                <div className="font-bold text-gray-800">{congrats.facebook || congrats.name}</div>
                <div className="text-sm text-gray-500">{maskPhone(form.phone)}</div>
              </div>
              <span className="ml-auto bg-emerald-100 text-emerald-700 text-xs font-bold px-2.5 py-1 rounded-full">{fLabel}</span>
            </div>

            <div className="bg-brand-50 border border-brand-100 rounded-xl px-4 py-2.5 text-sm">
              <span className="text-gray-500">Thuộc Team thủ lĩnh: </span>
              <span className="font-semibold text-brand-700">{leaderDisplay || '—'}</span>
            </div>

            <div className="grid grid-cols-1 gap-1.5 text-sm bg-gray-50 rounded-xl p-3">
              <Row label="Cấp trên trực tiếp" value={parentDisplay || '—'} />
              <Row label="F1 trực tiếp" value={String(congrats.f1Count ?? 0)} />
              <Row label="Ngày tham gia" value={form.joinDate ? form.joinDate.split('-').reverse().join('/') : '—'} />
              <Row label="Ngày sinh" value={form.birthday ? form.birthday.split('-').reverse().join('/') : '—'} />
              <Row label="Tỉnh thành" value={form.city || '—'} />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
              <b>Hồ sơ đang chờ duyệt.</b> Công ty sẽ <b>gọi điện xác minh</b> & kích hoạt tài khoản.
              Vui lòng tag tuyến trên của bạn để được duyệt nhanh hơn.
            </div>

            <button onClick={onBackToLogin} className="w-full bg-brand-600 text-white py-3 rounded-xl font-semibold hover:bg-brand-700 transition-colors">
              Về trang đăng nhập
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Màn thành công CẬP NHẬT (update) ----------
  if (doneMsg) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl max-w-md w-full p-8 text-center animate-fade-in">
          <CheckCircle className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-800">Hoàn tất</h2>
          <p className="text-gray-600 mt-2 text-sm">{doneMsg}</p>
          <button onClick={() => (onDone ? onDone() : onBackToLogin && onBackToLogin())} className="mt-5 w-full bg-brand-600 text-white py-3 rounded-xl font-semibold hover:bg-brand-700">
            Quay lại
          </button>
        </div>
      </div>
    );
  }

  // ---------- FORM ----------
  const inputCls = (k) => `w-full px-4 py-3 rounded-xl border text-[16px] focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none ${errors[k] ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50'}`;
  const Err = ({ k }) => (errors[k] ? <p className="text-red-500 text-xs mt-1">{errors[k]}</p> : null);

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-emerald-50 py-6 px-4">
      <div className="max-w-md mx-auto animate-fade-in">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-600 to-emerald-500 text-white text-center py-6 px-6 relative">
            {onBackToLogin && (
              <button onClick={onBackToLogin} className="absolute left-4 top-5 text-white/90 hover:text-white" title="Quay lại">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-2">
              <User className="w-7 h-7" />
            </div>
            <h1 className="text-xl font-bold">{isUpdate ? 'Cập nhật thông tin' : 'Đăng ký đại lý mới'}</h1>
            <p className="text-brand-50 mt-1 text-sm">{isUpdate ? 'Chỉnh sửa hồ sơ của bạn' : 'Hoàn thiện thông tin để tham gia'}</p>
          </div>

          <div className="p-6 space-y-5">
            {/* 0. SĐT (khoá) */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-1.5"><NumberBadge n="0" color="#0ea5e9" />Số điện thoại</label>
              <div className="relative">
                <Phone className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={form.phone} disabled className="w-full pl-9 pr-9 py-3 rounded-xl border border-gray-200 bg-gray-100 text-gray-500 text-[16px]" />
                <Lock className="w-4 h-4 text-gray-300 absolute right-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            {/* Ảnh đại diện */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Ảnh đại diện</label>
              <input ref={avatarRef} type="file" accept="image/*" onChange={handleAvatar} className="hidden" id="avatar-input" disabled={loading || isUploading} />
              <label htmlFor="avatar-input" className={`flex items-center justify-center w-full h-28 rounded-2xl border-2 border-dashed cursor-pointer transition-colors ${errors.avatar ? 'border-red-400 bg-red-50' : 'border-brand-200 bg-brand-50/40 hover:border-brand-400'}`}>
                {(avatarPreview || form.avatar) ? (
                  <div className="relative w-24 h-24">
                    <img src={avatarPreview || form.avatar} alt="avatar" className="w-24 h-24 rounded-full object-cover" />
                    {isUploading && <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center"><div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" /></div>}
                  </div>
                ) : (
                  <div className="text-center text-gray-500">
                    <Upload className="w-7 h-7 mx-auto mb-1 text-brand-400" />
                    <span className="text-sm">{isUploading ? 'Đang tải...' : 'Tải ảnh lên'}</span>
                  </div>
                )}
              </label>
              <Err k="avatar" />
            </div>

            {/* 1. Họ và tên */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-1.5"><NumberBadge n="1" color="#ef4444" />Họ và tên <span className="text-red-500 ml-1">*</span></label>
              <p className="text-xs text-gray-400 mb-1.5">Nhập đầy đủ họ tên theo CCCD</p>
              <input value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Họ tên của bạn" className={inputCls('name')} disabled={loading} />
              <Err k="name" />
            </div>

            {/* 2. Ngày sinh */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-1.5"><NumberBadge n="2" color="#22c55e" />Ngày - tháng - năm sinh <span className="text-red-500 ml-1">*</span></label>
              <p className="text-xs text-gray-400 mb-1.5">Chọn Ngày / Tháng / Năm — chọn năm trực tiếp, không phải bấm lùi</p>
              <DateSelect value={form.birthday} onChange={(v) => setField('birthday', v)} yearFrom={1940} yearTo={CUR_YEAR} disabled={loading} />
              <Err k="birthday" />
            </div>

            {/* 3. Tên hay dùng */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-1.5"><NumberBadge n="3" color="#3b82f6" />Tên hay dùng <span className="text-red-500 ml-1">*</span></label>
              <p className="text-xs text-gray-400 mb-1.5">Tên bạn hay dùng cho Facebook, Zalo v.v.</p>
              <input value={form.facebook} onChange={(e) => setField('facebook', e.target.value)} placeholder="Nhập tên hay dùng của bạn" className={inputCls('facebook')} disabled={loading} />
              <Err k="facebook" />
            </div>

            {/* 4. SĐT cấp trên */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-1.5"><NumberBadge n="4" color="#14b8a6" />Số điện thoại cấp trên trực tiếp <span className="text-red-500 ml-1">*</span></label>
              <p className="text-xs text-gray-400 mb-1.5">Nhập SĐT để tìm cấp trên trực tiếp</p>
              <input value={form.parentPhone} onChange={(e) => setField('parentPhone', e.target.value)} placeholder="Số điện thoại cấp trên trực tiếp" inputMode="numeric" className={inputCls('parentPhone')} disabled={loading} />
              {parentLookupState === 'loading' && <p className="text-xs text-gray-400 mt-1">Đang tìm cấp trên...</p>}
              <Err k="parentPhone" />
            </div>

            {/* 5. Cấp trên (auto) */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-1.5"><NumberBadge n="5" color="#8b5cf6" />Cấp trên trực tiếp</label>
              <div className={`w-full px-4 py-3 rounded-xl border text-[16px] flex items-center gap-2 ${parentDisplay ? 'border-brand-200 bg-brand-50 text-brand-700 font-semibold' : 'border-gray-200 bg-gray-100 text-gray-400'}`}>
                {parentDisplay || 'Tên cấp trên sẽ được tự động điền'}
              </div>
            </div>

            {/* 6. Thủ lĩnh (auto) */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-1.5"><NumberBadge n="6" color="#ec4899" /><Crown className="w-4 h-4 mr-1 text-pink-500" />Thủ lĩnh đứng đầu Team (F1 Công ty)</label>
              <div className={`w-full px-4 py-3 rounded-xl border text-[16px] flex items-center gap-2 ${leaderDisplay ? 'border-brand-200 bg-brand-50 text-brand-700 font-semibold' : 'border-gray-200 bg-gray-100 text-gray-400'}`}>
                {leaderDisplay || 'Thủ lĩnh Team sẽ tự động hiển thị'}
              </div>
            </div>

            {/* 7. Ngày tham gia */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-1.5"><NumberBadge n="7" color="#64748b" />Ngày tham gia công ty <span className="text-red-500 ml-1">*</span></label>
              {joinDateLocked
                ? <p className="text-xs text-gray-500 mb-1.5 flex items-center gap-1"><Lock className="w-3.5 h-3.5" />Công ty đã phê duyệt ngày tham gia — không tự sửa được. Cần sửa vui lòng liên hệ công ty.</p>
                : <p className="text-xs text-amber-600 mb-1.5">⚠️ Chọn đúng ngày — ảnh hưởng thưởng sổ tiết kiệm, thâm niên</p>}
              <DateSelect value={form.joinDate} onChange={(v) => setField('joinDate', v)} yearFrom={2010} yearTo={CUR_YEAR} disabled={loading || joinDateLocked} />
              <Err k="joinDate" />
            </div>

            {/* 8. Tỉnh thành */}
            <div>
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-1.5"><NumberBadge n="8" color="#16a34a" /><MapPin className="w-4 h-4 mr-1 text-green-600" />Tỉnh thành <span className="text-red-500 ml-1">*</span></label>
              <select value={form.city} onChange={(e) => setField('city', e.target.value)} className={inputCls('city')} disabled={loading}>
                <option value="">Chọn tỉnh thành</option>
                {cities.map((c) => <option key={c.id || c.code || c.name} value={c.name}>{c.name}</option>)}
              </select>
              <Err k="city" />
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}

            <button
              onClick={isUpdate ? handleSubmitUpdate : handleSubmitRegister}
              disabled={loading}
              className="w-full bg-gradient-to-r from-brand-600 to-emerald-500 text-white py-3.5 rounded-xl font-bold text-base hover:opacity-95 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-brand-600/20"
            >
              {loading ? (<><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />Đang xử lý...</>)
                : (<>{isUpdate ? 'Lưu thông tin' : 'Đăng ký'}<CheckCircle className="w-5 h-5" /></>)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium text-gray-800">{value}</span>
  </div>
);

export default RegisterPage;
